import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { Text } from "@swa-ui/core";
import { useFormContext } from "@swa-ui/form";
import i18n from "@swa-ui/locale";

import {
  AVAILABLE,
  ERRORED,
  NOT_VALIDATED,
  SERVICE_UNAVAILABLE,
  UNAVAILABLE,
  VALIDATING,
} from "../../defines/usernameAvailabilityStatus";
import { useCheckUsernameAvailabilityServices } from "../../useCheckUsernameAvailabilityServices";
import { UsernameFormField } from "../UsernameFormField";

/**
 * NewUsernameFormField will be used when the user is creating a new username for an account.
 * It will be used on enrollment and when adding missing information on login.
 */
export const NewUsernameFormField = (props) => {
  const {
    className,
    componentProps = {},
    defaultValue,
    disabled,
    label,
    name = "username",
    onUsernameAvailabilityStatusChange,
    value,
  } = props;
  const formMethods = useFormContext();
  const [availabilityStatus, setAvailabilityStatus] = useState(NOT_VALIDATED);
  const lastUsernameChecked = React.useRef(null);
  const { checkUsernameAvailability, loading } = useCheckUsernameAvailabilityServices();

  useEffect(() => {
    updateAvailabilityStatus(NOT_VALIDATED);
  }, []);

  useEffect(() => {
    if (availabilityStatus === VALIDATING) {
      handleCheckAvailability();
    } else if (availabilityStatus !== NOT_VALIDATED) {
      formMethods.trigger(name);
    }
  }, [availabilityStatus]);

  return <UsernameFormField {...getUsernameProps()} />;

  function getUsernameProps() {
    return {
      caption: renderUsernameAvailability(),
      className,
      componentProps: {
        ...componentProps,
        defaultValue,
        onBlur: handleUsernameChangeValidation,
      },
      disabled: loading || disabled,
      label,
      name,
      option: {
        onClick: handleUsernameChangeValidation,
        text: i18n("NewUsernameFormField__CHECK_AVAILABILITY"),
      },
      value,
    };
  }

  function handleUsernameChangeValidation() {
    const username = formMethods.getValues(name);
    const hasChanged = lastUsernameChecked.current !== username;

    if (hasChanged) {
      updateAvailabilityStatus(VALIDATING);
    }
  }

  function renderUsernameAvailability() {
    return (
      availabilityStatus === AVAILABLE && (
        <Text color="var(--username-available)">
          {i18n("NewUsernameFormField__TEXT_USERNAME_AVAILABLE")}
        </Text>
      )
    );
  }

  async function handleCheckAvailability() {
    const isValid = await formMethods.trigger(name);
    const username = formMethods.getValues(name);

    if (isValid) {
      lastUsernameChecked.current = username;

      const { callStatus } = await checkUsernameAvailability(username);
      let status;

      if (callStatus.ok) {
        status = AVAILABLE;
      } else {
        if (callStatus.status === 400) {
          status = UNAVAILABLE;
        } else if (callStatus.status === 503) {
          status = SERVICE_UNAVAILABLE;
          lastUsernameChecked.current = undefined;
        } else {
          status = ERRORED;
          lastUsernameChecked.current = undefined;
        }
      }

      updateAvailabilityStatus(status);
    } else {
      updateAvailabilityStatus(NOT_VALIDATED);
    }
  }

  function updateAvailabilityStatus(status) {
    onUsernameAvailabilityStatusChange(status);
    setAvailabilityStatus(status);
  }
};

NewUsernameFormField.propTypes = {
  /* * The className for the component.
   */
  className: PropTypes.string,

  /**
   * The props to be passed to the component.
   */
  componentProps: PropTypes.object,

  /**
   * The default value of the form field.
   */
  defaultValue: PropTypes.string,

  /**
   * Whether the form field is disabled.
   */
  disabled: PropTypes.bool,

  /**
   * The label of the form field.
   */
  label: PropTypes.string,

  /**
   * The name of the form field.
   */
  name: PropTypes.string,

  /**
   * Callback function when the availability status changes.
   */
  onUsernameAvailabilityStatusChange: PropTypes.func.isRequired,

  /**
   * The value of the form field.
   */
  value: PropTypes.string,
};
